import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Header from "@/components/Header";
import { createApiService } from "@/App";
import { useWallet } from "@/context/WalletContext";
import {
  Search,
  Bot,
  TrendingUp,
  Eye,
  Zap,
  Activity,
  BarChart3,
  Coins,
  Brain,
  RefreshCw,
  ExternalLink,
  Copy,
  CheckCircle,
  Globe,
  Server,
  X
} from "lucide-react";
import { toast } from "sonner";

// Capability icons and colors
const capabilityConfig = {
  defi_trading: { icon: TrendingUp, color: "text-green-400", bg: "bg-green-500/10" },
  whale_tracking: { icon: Eye, color: "text-purple-400", bg: "bg-purple-500/10" },
  price_monitoring: { icon: Activity, color: "text-blue-400", bg: "bg-blue-500/10" },
  yield_farming: { icon: Coins, color: "text-yellow-400", bg: "bg-yellow-500/10" },
  arbitrage: { icon: Zap, color: "text-orange-400", bg: "bg-orange-500/10" },
  stxoshi_transfer: { icon: BarChart3, color: "text-cyan-400", bg: "bg-cyan-500/10" },
  market_intelligence: { icon: Brain, color: "text-pink-400", bg: "bg-pink-500/10" }
};

export default function AgentDirectory() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useWallet();
  const apiService = createApiService(getAuthHeaders);

  const [loading, setLoading] = useState(true);
  const [platformCard, setPlatformCard] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedCapability, setSelectedCapability] = useState("all");
  const [agents, setAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [modalCardData, setModalCardData] = useState(null);
  const [modalCardTitle, setModalCardTitle] = useState("");

  // Fetch platform agent card
  const fetchPlatformCard = useCallback(async () => {
    try {
      const response = await apiService.get("/a2a/agent-card");
      setPlatformCard(response.data);
    } catch (err) {
      console.error("Failed to fetch platform card:", err);
    }
  }, []); // Remove apiService dependency - it's stable

  // Fetch capabilities
  const fetchCapabilities = useCallback(async () => {
    try {
      const response = await apiService.get("/a2a/capabilities");
      setCapabilities(response.data.capabilities || []);
    } catch (err) {
      console.error("Failed to fetch capabilities:", err);
    }
  }, []); // Remove apiService dependency - it's stable

  // Discover agents by capability
  const discoverAgents = useCallback(async (capability) => {
    setLoading(true);
    try {
      const params = capability && capability !== "all" 
        ? `?capability=${capability}&min_reliability=0` 
        : "?capability=defi_trading&min_reliability=0";
      
      const response = await apiService.get(`/a2a/discover${params}`);
      setAgents(response.data.agents || []);
    } catch (err) {
      console.error("Failed to discover agents:", err);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []); // Remove apiService dependency - it's stable

  useEffect(() => {
    fetchPlatformCard();
    fetchCapabilities();
    discoverAgents(selectedCapability);
  }, [selectedCapability]); // Only re-run when selectedCapability changes

  // Copy agent ID to clipboard
  const copyAgentId = (agentId) => {
    navigator.clipboard.writeText(agentId);
    setCopiedId(agentId);
    toast.success("Agent ID copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Show agent card in modal
  const showAgentCard = async (type) => {
    try {
      let data, title;
      if (type === "platform") {
        data = platformCard;
        title = "Platform Agent Card";
      } else if (type === "m2m") {
        const resp = await apiService.get("/m2m/actions");
        data = resp.data;
        title = "M2M Actions";
      } else if (type === "acp") {
        const resp = await apiService.get("/acp/manifest");
        data = resp.data;
        title = "ACP Manifest";
      }
      setModalCardData(data);
      setModalCardTitle(title);
      setShowCardModal(true);
    } catch (err) {
      toast.error("Failed to load data");
    }
  };

  // Filter agents by search
  const filteredAgents = agents.filter(agent => 
    agent.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.agent_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="p-4 md:p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                <Globe className="w-8 h-8 text-primary" />
                A2A Agent Directory
              </h1>
              <p className="text-muted-foreground mt-1">
                Discover and interact with AI agents using the Agent2Agent protocol
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => window.open("/api/a2a/agent-card", "_blank")}
              className="gap-2"
            >
              <Server className="w-4 h-4" />
              View Platform Agent Card
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>

          {/* Platform Card Summary */}
          {platformCard && (
            <Card className="glass-card border-primary/30 bg-primary/5">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Bot className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl font-bold">{platformCard.name}</h2>
                      <p className="text-sm text-muted-foreground">{platformCard.description?.slice(0, 100)}...</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">v{platformCard.version}</Badge>
                        <Badge variant="outline" className="text-xs text-green-400 border-green-400">
                          {platformCard.capabilities?.length} Capabilities
                        </Badge>
                        <Badge variant="outline" className="text-xs text-blue-400 border-blue-400">
                          {platformCard.skills?.length} Skills
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {platformCard.metadata?.a2a_compatible && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        A2A Compatible
                      </Badge>
                    )}
                    {platformCard.metadata?.m2m_enabled && (
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                        M2M Enabled
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Capabilities Grid */}
          <div>
            <h3 className="font-heading font-semibold mb-3">Platform Capabilities</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {capabilities.map((cap) => {
                const config = capabilityConfig[cap.id] || { icon: Bot, color: "text-gray-400", bg: "bg-gray-500/10" };
                const Icon = config.icon;
                const isSelected = selectedCapability === cap.id;
                
                return (
                  <Card
                    key={cap.id}
                    className={`cursor-pointer transition-all hover:scale-105 ${
                      isSelected ? "ring-2 ring-primary" : ""
                    } ${config.bg}`}
                    onClick={() => setSelectedCapability(cap.id)}
                  >
                    <CardContent className="p-3 text-center">
                      <Icon className={`w-6 h-6 mx-auto mb-2 ${config.color}`} />
                      <p className="text-xs font-medium truncate">
                        {cap.name}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search agents by name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Tabs value={selectedCapability} onValueChange={setSelectedCapability}>
              <TabsList>
                <TabsTrigger value="all" onClick={() => discoverAgents("defi_trading")}>
                  All Agents
                </TabsTrigger>
                <TabsTrigger value="whale_tracking" onClick={() => discoverAgents("whale_tracking")}>
                  Whale Trackers
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Agents List */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-primary" />
                  Discovered Agents
                </span>
                <Badge variant="outline">{filteredAgents.length} agents</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="text-center py-12">
                  <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">No Agents Found</h3>
                  <p className="text-sm text-muted-foreground">
                    No agents match your current filters. Try selecting a different capability.
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3 pr-4">
                    {filteredAgents.map((agent, idx) => {
                      const reliability = agent.reliability || 0;
                      const reliabilityColor = reliability >= 0.7 
                        ? "text-green-400" 
                        : reliability >= 0.4 
                          ? "text-yellow-400" 
                          : "text-red-400";
                      
                      return (
                        <Card
                          key={agent.agent_id || idx}
                          className="bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                                  <Bot className="w-5 h-5 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold truncate">
                                      {agent.name || agent.agent_id?.slice(0, 20)}
                                    </h4>
                                    <Badge 
                                      variant="outline" 
                                      className={`text-xs ${
                                        agent.status === "running" 
                                          ? "text-green-400 border-green-400" 
                                          : "text-gray-400"
                                      }`}
                                    >
                                      {agent.status || "unknown"}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground font-mono truncate">
                                    {agent.agent_id}
                                  </p>
                                  <div className="flex items-center gap-4 mt-2 text-xs">
                                    <span className={reliabilityColor}>
                                      Reliability: {(reliability * 100).toFixed(0)}%
                                    </span>
                                    {agent.metadata?.win_rate !== undefined && (
                                      <span className="text-muted-foreground">
                                        Win Rate: {agent.metadata.win_rate.toFixed(1)}%
                                      </span>
                                    )}
                                    {agent.metadata?.total_trades !== undefined && (
                                      <span className="text-muted-foreground">
                                        Trades: {agent.metadata.total_trades}
                                      </span>
                                    )}
                                    {agent.metadata?.pnl_pct !== undefined && (
                                      <span className={agent.metadata.pnl_pct >= 0 ? "text-green-400" : "text-red-400"}>
                                        P&L: {agent.metadata.pnl_pct >= 0 ? "+" : ""}{agent.metadata.pnl_pct.toFixed(2)}%
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyAgentId(agent.agent_id)}
                                  className="h-8 w-8 p-0"
                                >
                                  {copiedId === agent.agent_id ? (
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const botId = agent.agent_id?.replace("stxoshi-bot-", "");
                                    if (botId) navigate(`/bot/${botId}`);
                                  }}
                                  className="gap-1"
                                >
                                  View
                                  <ExternalLink className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Protocol Info Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="glass-card">
              <CardContent className="p-4">
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-green-400" />
                  A2A Protocol
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Google's Agent2Agent protocol for agent discovery and task management.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => showAgentCard("platform")}
                >
                  View Agent Card
                </Button>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardContent className="p-4">
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <Server className="w-4 h-4 text-blue-400" />
                  M2M API
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Machine-to-machine endpoints for autonomous agent interactions.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => showAgentCard("m2m")}
                >
                  View Actions
                </Button>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardContent className="p-4">
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-purple-400" />
                  ACP Manifest
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  IBM's Agent Communication Protocol for interoperability.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => showAgentCard("acp")}
                >
                  View Manifest
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Agent Card Modal */}
          <Dialog open={showCardModal} onOpenChange={setShowCardModal}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  {modalCardTitle}
                </DialogTitle>
              </DialogHeader>
              <ScrollArea className="h-[60vh] pr-4">
                {modalCardData && (
                  <div className="space-y-4">
                    {modalCardData.id && (
                      <div>
                        <p className="text-xs text-muted-foreground">ID</p>
                        <p className="font-mono text-sm">{modalCardData.id}</p>
                      </div>
                    )}
                    {modalCardData.name && (
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="font-semibold">{modalCardData.name}</p>
                      </div>
                    )}
                    {modalCardData.description && (
                      <div>
                        <p className="text-xs text-muted-foreground">Description</p>
                        <p className="text-sm">{modalCardData.description}</p>
                      </div>
                    )}
                    {modalCardData.version && (
                      <div>
                        <p className="text-xs text-muted-foreground">Version</p>
                        <Badge variant="outline">{modalCardData.version}</Badge>
                      </div>
                    )}
                    {modalCardData.capabilities && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Capabilities</p>
                        <div className="flex flex-wrap gap-2">
                          {modalCardData.capabilities.map((cap, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{cap}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {modalCardData.skills && modalCardData.skills.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Skills</p>
                        <div className="space-y-2">
                          {modalCardData.skills.map((skill, i) => (
                            <div key={i} className="bg-muted/30 p-2 rounded">
                              <p className="font-medium text-sm">{skill.name}</p>
                              <p className="text-xs text-muted-foreground">{skill.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {modalCardData.actions && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Available Actions</p>
                        <div className="space-y-2">
                          {modalCardData.actions.map((action, i) => (
                            <div key={i} className="bg-muted/30 p-2 rounded">
                              <p className="font-mono text-sm text-primary">{action.id}</p>
                              <p className="text-xs text-muted-foreground">{action.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {modalCardData.endpoints && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Endpoints</p>
                        <div className="space-y-1">
                          {Object.entries(modalCardData.endpoints).map(([key, value], i) => (
                            <div key={i} className="flex justify-between items-center bg-muted/30 p-2 rounded">
                              <span className="text-xs font-medium">{key}</span>
                              <span className="text-xs font-mono text-muted-foreground truncate max-w-[300px]">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {modalCardData.metadata && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Metadata</p>
                        <pre className="bg-muted/30 p-2 rounded text-xs overflow-x-auto">
                          {JSON.stringify(modalCardData.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
